import type { CaptureResult } from 'posthog-js';

import { getAnalyticsEnvironment } from './env';
import { hasAnalyticsOptOutFromDocument, hasDoNotTrackEnabled } from './opt-out';
import { sanitizeHref, sanitizePathname, sanitizeUrl } from './sanitize';

const allowedEvents = new Set([
  '$pageview',
  '$pageleave',
  '$web_vitals',
  '$identify',
  '$set',
  '$opt_in',
  '$opt_out',
  'marketing_cta_clicked',
  'calculator_updated',
]);

const sensitiveKeys = new Set([
  'cid',
  'email',
  'iban',
  'name',
  'phone',
  'phoneNumber',
  'birthdate',
  'dateOfBirth',
  'address',
  'street',
  'firstName',
  'lastName',
  'holderName',
  'account_holder',
  'message',
  'propertyPath',
]);

function sanitizeValue(key: string, value: unknown): unknown {
  if (sensitiveKeys.has(key) || key === '$elements') {
    return undefined;
  }

  if (typeof value === 'string') {
    const normalizedKey = key.toLowerCase();

    if (normalizedKey.includes('pathname')) {
      return sanitizePathname(value);
    }

    if (normalizedKey.includes('url') || normalizedKey.includes('referrer')) {
      return sanitizeUrl(value);
    }

    if (normalizedKey === 'href') {
      return sanitizeHref(value);
    }
  }

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeValue('', item));
  }

  if (value && typeof value === 'object') {
    return sanitizeProperties(value);
  }

  return value;
}

function sanitizeProperties(properties: object, includeEnvironment = false) {
  const sanitized: Record<string, unknown> = {};

  if (includeEnvironment) {
    sanitized.environment = getAnalyticsEnvironment();
  }

  for (const [key, value] of Object.entries(properties)) {
    const nextValue = sanitizeValue(key, value);

    if (nextValue !== undefined) {
      sanitized[key] = nextValue;
    }
  }

  return sanitized;
}

function filterEvent(event: CaptureResult) {
  if (
    !allowedEvents.has(event.event) ||
    hasAnalyticsOptOutFromDocument() ||
    hasDoNotTrackEnabled()
  ) {
    return null;
  }

  return {
    ...event,
    properties: sanitizeProperties(event.properties, true),
  };
}

export function beforeSend(event: CaptureResult | null): CaptureResult | null {
  if (!event) {
    return null;
  }

  return filterEvent(event);
}
